import { Fragment, useEffect, useRef, useState, useContext } from "react";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassFoldUdBox, KeyValueLine, KeyValuesBoxGrid, KeyValuesHorizontal, PropertyTextDoc, ValueGroup, ViewButtonIcon, ViewButtonText } from "./PropertyComponents";
import { GlassButton } from "src/components/GlassButtons";
import { PropertyData } from "./PropertyInfoCollectorTypes";
import { getTingbogsDokLink, TingBogRecordNumber } from "./PropertyInfoGroupFormatters";
import { Spinner } from "react-bootstrap";
import { GlassInfo } from "src/components/MitGlassComponents";
import { BiBug } from "react-icons/bi";
import { BsFileEarmarkPdf } from "react-icons/bs";
import { Persistence } from "src/managers/Persistence";
import { MapitStateContext, MapitStateActionType } from "src/states/MapitState";
import { PropertyInfoInterface } from 'src/managers/PropertyInfoInterface';
import { parse as WKTparse } from 'wkt';
import { GenerateGeomUtils } from 'src/managers/GenerateGeomUtils';
import { BFELookup } from 'src/managers/SearchBarGeneralPlugin';
import polylabel from 'polylabel';
import { DC } from "src/dataCollector/DataCollector";
import { message } from "src/components/Message";
import { ServitutAnnotationsTable } from "src/modules/ANNModule/ANNComponents";
import { ANNLLMAnalyzer } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { Feature } from "src/states/ApplicationStateFeatures";

export function ServitutDetailsView(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   type ViamapTingBog = NonNullable<PropertyData["ViamapTingBog"]>;
   type Servitut = ViamapTingBog["servitutter"][number] & { dokumentFilnavn?: string };
   type StamOplysninger = ViamapTingBog["stamOplysninger"];

   const servitut = props.managedWindow.windowParams?.servitut as Servitut | undefined;
   const stamOplysninger = props.managedWindow.windowParams?.stamOplysninger as StamOplysninger | undefined;
   const viamapTingBog = props.managedWindow.windowParams?.viamapTingBog as ViamapTingBog | undefined;
   const propertyData = props.managedWindow.windowParams?.propertyData as PropertyData | undefined;
   console.log("servitut", servitut);

   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [xmlDoc, setXmlDoc] = useState<Document | null>(null);
   const [downloadingPdf, setDownloadingPdf] = useState(false);

   async function fetchServitutDetailsByUuid(uuid: string, abortSignal: AbortSignal) {
      setLoading(true);
      setError(null);
      DC.TL.DokumentTingbog(uuid).then((data) => {
         if (abortSignal.aborted) return;
         setXmlDoc(data);
      }).catch((err) => {
         if (abortSignal.aborted) return;
         setXmlDoc(null);
         setError(err.message || String(err));
      }).finally(() => {
         setLoading(false);
      });
   }

   useEffect(() => {
      if (!servitut?.dokumentId) return;
      let abort = new AbortController();
      fetchServitutDetailsByUuid(servitut.dokumentId, abort.signal);
      return () => {
         abort.abort();
      };
   }, [servitut?.dokumentId]);

   if (!servitut) {
      return (
         <AdvancedDragModal
            window={props.managedWindow}
            title={Localization.getText("Servitutdetails")}
            onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
            allowFullscreen={true}
            allowUserMiniMize={true}
            PosDefault={{ width: "800px", height: "520px", left: "10%", top: "10%" }}
         >
            <ADModalBody>
               <GlassInfo>
                  {Localization.getText("Missing context for servitut details") || "Missing context for servitut details"}
               </GlassInfo>
            </ADModalBody>
         </AdvancedDragModal>
      );
   }

   let servitutTingbogLink = getTingbogsDokLink(servitut);
   const annotationDocumentId = servitut?.dokumentId || servitut?.aktAliasId || "";
   const aktIdForDatoloebenr = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
   const datoloebenr = aktIdForDatoloebenr ? TingBogRecordNumber(aktIdForDatoloebenr) : undefined;

   // Helper function to check if a servitut is digital (after 2009)
   // datoloebenr format: DDMMYYYY-XXXXX
   const isServitutDigital = (datoloebenr?: string): boolean => {
      if (!datoloebenr) return false;
      // Extract year from DDMMYYYY-XXXXX format (positions 4-7)
      const yearMatch = datoloebenr.match(/^(\d{2})(\d{2})(\d{4})-/);
      if (yearMatch) {
         const year = parseInt(yearMatch[3], 10);
         return year > 2009;
      }
      return false;
   };

   // Download endpoint should only be used when we have the actual scanned akt id.
   // In Tingbog data this is represented by `dokumentFilnavn`.
   const aktIdForPdf = (servitut as any)?.dokumentFilnavn as string | undefined;
   const hasPdf = Boolean(aktIdForPdf);

   async function downloadPdf() {
      if (!aktIdForPdf || downloadingPdf) return;

      setDownloadingPdf(true);
      try {
         const analyzer = new ANNLLMAnalyzer();
         const blob = await analyzer.fetchPdf(aktIdForPdf);
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `akt_${aktIdForPdf}.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (err: any) {
         message.error(err.message || Localization.getText("Failed to download PDF"));
      } finally {
         setDownloadingPdf(false);
      }
   }

   return (
      <AdvancedDragModal
         window={props.managedWindow}
         title={Localization.getText("Servitutdetails")}
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ width: "800px", height: "520px", left: "10%", top: "10%" }}
         subHeaderContent={
            <>
               {SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ? (
                  <>
                     <ViewButtonIcon onClick={() => console.info(xmlDoc)} ><BiBug /><span style={{ paddingInline: "5px 5px" }} >Log to Console</span></ViewButtonIcon>
                  </>
               ) : null}
               {hasPdf && !isServitutDigital(datoloebenr) ? (
                  <ViewButtonIcon
                     title={Localization.getText("Download PDF")}
                     disabled={downloadingPdf}
                     onClick={() => {
                        void downloadPdf();
                     }}
                  >
                     {downloadingPdf ? <Spinner animation="border" size="sm" role="status" style={{ marginRight: "5px" }} /> : <BsFileEarmarkPdf />}
                     <span style={{ paddingInline: "5px" }}>{downloadingPdf ? Localization.getText("Downloading...") : Localization.getText("Download PDF")}</span>
                  </ViewButtonIcon>
               ) : null}
               <ViewButtonIcon
                  title={servitutTingbogLink ? undefined : Localization.getText("No link available")}
                  disabled={!servitutTingbogLink}
                  onClick={() => {
                     window.open(servitutTingbogLink, "_blank");
                  }}
               >
                  <span style={{ paddingInline: "5px" }}>Åbn på Tinglysning.dk</span>
               </ViewButtonIcon>
            </>
         }
      >
         <ADModalBody >
            <ServitutHeader servitut={servitut} xmlDoc={xmlDoc} />
            {loading ? <ShowLoading link={servitutTingbogLink} /> : null}
            {error ? <ShowError /> : null}
            {xmlDoc ? (
               <>
                  <ShowTinglysningMessage xmlDoc={xmlDoc} link={servitutTingbogLink} />
                  <ServitutEjendomme servitutXDOC={xmlDoc} servitut={servitut} />
                  <ServitutTekst servitutXDOC={xmlDoc} />
                  <BilagsVisning servitutXDOC={xmlDoc} />
                  <ProtectedFeature feature={Feature.AnnotationModule} contentsIfNoAccess={<></>}>
                     <ServitutAnnotationsTable
                        documentId={annotationDocumentId}
                        datoloebenr={datoloebenr}
                        servitut={servitut}
                        stamOplysninger={stamOplysninger}
                        xmlDoc={xmlDoc}
                        viamapTingBog={viamapTingBog}
                        propertyData={propertyData}
                     />
                  </ProtectedFeature>
               </>
            ) : null}
         </ADModalBody>
      </AdvancedDragModal >
   );
}

function ShowLoading({ link }: { link?: string }) {
   let textRef = useRef<HTMLDivElement>(null);
   let [showQuickButton, setShowQuickButton] = useState(false);

   useEffect(() => {
      if (!textRef.current) return;
      const box = textRef.current;
      function updateText() {
         box.innerText = Localization.getText("The action takes longer than expected");
         setShowQuickButton(true);
      }
      const interval = setTimeout(() => updateText(), 5000);
      box.innerText = Localization.getText("Loading");
      return () => {
         clearTimeout(interval);
      }
   }, [textRef]);

   return (
      <GlassInfo>
         <div ref={textRef} style={{ padding: 8 }} />
         <Spinner animation="border" role="status" />
         {showQuickButton && link ? <><br /><ViewButtonText style={{ marginTop: "1em" }}
            onClick={() => {
               window.open(link, "_blank");
            }}

         >Åbn på Tinglysning.DK</ViewButtonText></> : null}
      </GlassInfo>
   )
}

function ShowError() {
   return (
      <GlassInfo>
         <div style={{ padding: 8, color: "var(--dangerColor)" }}>{Localization.getText("Failed to fetch data")}</div>
      </GlassInfo>
   )
}

function ShowTinglysningMessage({ xmlDoc, link }: { xmlDoc: Document | null, link?: string }) {
   if (!(xmlDoc instanceof Document) || xmlDoc.children[0]?.children.length !== 0) {
      return <></>
   }
   return (
      <GlassInfo>
         {xmlDoc?.children[0].textContent}
         {link ? <><br /><ViewButtonText style={{ marginTop: "1em" }}
            onClick={() => {
               window.open(link, "_blank");
            }}
         >Åbn på Tinglysning.DK</ViewButtonText></> : null}
      </GlassInfo>
   )
}

function ServitutHeader({ servitut, xmlDoc }: { servitut: (PropertyData["ViamapTingBog"] & {})["servitutter"][number], xmlDoc?: Document | null, originalTinglysningsDato?: string }) {

   const formatDate = (val?: string) => {
      return val ? new Date(val).toLocaleDateString(Localization.getLocale(), { year: 'numeric', month: '2-digit', day: '2-digit' }) : "";
   };

   const normalizeDatePart = (val?: string | null) => (val?.split("T")[0] || "");

   const documentAliasIdentifikator = (xmlDoc?.getElementsByTagName("DokumentAliasIdentifikator")[0]?.textContent?.split("-")[0]) || "";
   let originalDateOnly: string | undefined;
   if (/^\d{8}$/.test(documentAliasIdentifikator)) {
      const y = documentAliasIdentifikator.slice(0, 4), m = documentAliasIdentifikator.slice(4, 6), d = documentAliasIdentifikator.slice(6, 8);
      originalDateOnly = formatDate(`${y}-${m}-${d}`);
   } else {
      originalDateOnly = servitut.tinglysningsDato ? formatDate(normalizeDatePart(servitut.tinglysningsDato)) : undefined;
   }

   const lastPåtegnetRaw = xmlDoc?.getElementsByTagName("TinglysningsDato")[0]?.textContent || "";
   const lastPåtegnetDateOnly = formatDate(normalizeDatePart(lastPåtegnetRaw));

   const shouldShowLastPåtegnet = Boolean(lastPåtegnetRaw) && (originalDateOnly !== lastPåtegnetDateOnly);

   console.log("xml", originalDateOnly);
   console.log("xml", lastPåtegnetDateOnly);

   const aktIdForDisplay = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
   return (
      <KeyValuesHorizontal >
         <KeyValueLine valKey={Localization.getText("Date/serial number")} value={aktIdForDisplay ? TingBogRecordNumber(aktIdForDisplay) : ""} />
         {shouldShowLastPåtegnet ? <KeyValueLine valKey={<span style={{ color: "var(--dangerColor)" }}>{Localization.getText("Last signed")}</span>} value={lastPåtegnetDateOnly} /> : null}
      </KeyValuesHorizontal >
   )
}

function ServitutEjendomme({ servitutXDOC, servitut }: { servitutXDOC: Document | null, servitut: any }) {
   const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const [creatingLayer, setCreatingLayer] = useState(false);
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }

   let Ejendomme = Array.from(servitutXDOC.getElementsByTagName("FastEjendomMedRettigheder"));
   if (Ejendomme.length === 0) {
      return <></>
   }

   async function handleAddAsLayer() {
      setCreatingLayer(true);
      try {
         const polygonFeatures: any[] = [];
         const markerFeatures: any[] = [];
         const missingAddressBFEs: string[] = [];
         const bfeLookupInstance = new BFELookup(() => { });

         for (const ejendom of Ejendomme) {
            const bfe = ejendom.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent || null;
            if (!bfe) {
               throw new Error('Servitut: missing BFE number in FastEjendomMedRettigheder element');
            }

            const aktIdForLabel = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
            const servitutLabel = aktIdForLabel ? TingBogRecordNumber(aktIdForLabel) : "";
            const baseProps: any = {
               servitutId: servitut?.dokumentId,
               servitutdatoløbenummer: servitutLabel,
               BFEnummer: bfe
            };

            // Adds polygons and markers to their lists for making layers later.
            const idNum = Number(bfe);
            if (!Number.isNaN(idNum)) {
               const suggestions = await bfeLookupInstance.calculateSuggestions({}, bfe.toString());
               if (suggestions.length === 1) {
                  const bfeWithoutSFE = suggestions.filter(s => s?.data?.type === 'Ejerlejlighed' || s?.data?.type === 'BPFG');
                  if (bfeWithoutSFE.length === 0) { //SFE
                     try {
                        const sfeGeo = await PropertyInfoInterface.getGeojsonOfSFE(idNum);
                        const sfeFeatures = (sfeGeo && Array.isArray(sfeGeo.features)) ? sfeGeo.features : [];
                        for (const sf of sfeFeatures) {
                           if (sf?.geometry && (sf.geometry.type === 'Polygon' || sf.geometry.type === 'MultiPolygon')) {
                              polygonFeatures.push({ type: 'Feature', geometry: sf.geometry, properties: { ...baseProps, ...(sf.properties || {}) } });
                           }
                        }
                     } catch (e) {
                        console.warn('Servitut: SFE polygon fetch error (skipping polygons) for', bfe, e);
                     }
                  }
                  else { //Ejerlejighed/BPFG
                     const coords = await handleCoordinates(bfeWithoutSFE[0].data);
                     if (!coords) {
                        missingAddressBFEs.push(bfe);
                        continue;
                     }
                     const propsBase = { BFEnummer: bfe };
                     markerFeatures.push({
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: [coords.lng, coords.lat] },
                        properties: { ...propsBase, Ejendomstype: bfeWithoutSFE[0].data.type }
                     });
                  }
               }
               else {
                  throw new Error('Servitut: multiple BFELookup suggestions for BFE number in FastEjendomMedRettigheder element: ' + bfe);
               }
            } else {
               throw new Error('Servitut: invalid BFE number in FastEjendomMedRettigheder element: ' + bfe);
            }
         }

         // Inform the user about BFEs without address, but continue with layers for others
         if (missingAddressBFEs.length > 0) {
            message.info(Localization.getText("One or more properties have no address. BFE") + ": " + missingAddressBFEs.join(', '));
         }

         if (polygonFeatures.length === 0 && markerFeatures.length === 0) {
            message.error('no geometries found (no polygons, no points)');
            return;
         }

         const aktIdForDatasetLabel = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
         const label = aktIdForDatasetLabel ? TingBogRecordNumber(aktIdForDatasetLabel) : "";
         const baseDatasetName = Localization.getText("Record") + ` ${label} ` + Localization.getText("registered");

         if (polygonFeatures.length > 0) {
            const polyFc = { type: 'FeatureCollection', features: polygonFeatures };
            Persistence.createLayersFromGeoJSON(`${baseDatasetName} - polygoner`, polyFc as any, new Date(), (li: any) => {
               mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: li, zoomToFocus: false } });
            });
         }

         if (markerFeatures.length > 0) {
            const markerFc = { type: 'FeatureCollection', features: markerFeatures };
            Persistence.createLayersFromGeoJSON(`${baseDatasetName} - punkter`, markerFc as any, new Date(), (li: any) => {
               mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: li, zoomToFocus: false } });
            });
         }

      } catch (err: any) {
         message.error(err.message || String(err));
      } finally {
         setCreatingLayer(false);
      }
   }

   return (
      <GlassFoldUdBox
         title={
            <>
               <span style={{ marginRight: 'auto' }}>{Localization.getText("Properties") + " (" + Ejendomme.length + ")"}</span>
               {creatingLayer ? <Spinner animation="border" size="sm" role="status" style={{ marginRight: 8 }} /> : null}
               <GlassButton
                  disabled={creatingLayer}
                  onMouseDown={(e: any) => { e.stopPropagation(); }}
                  onClick={(e: any) => { e.stopPropagation(); void handleAddAsLayer(); }}
               >
                  {Localization.getText("Add as layer")}
               </GlassButton>
            </>
         }
         foldetUd={false}>
         {Ejendomme.map((ejendom, index) => (
            <Fragment key={index}>
               <ValueGroup>
                  <KeyValueLine valKey={"BFEnummer"} value={ejendom.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent || ""} />
                  <KeyValuesBoxGrid minWidth={"150px"} >
                     {Array.from(ejendom.getElementsByTagName("Matrikel")).map((matrikel, idx) => {
                        const name = matrikel.getElementsByTagName("CadastralDistrictName")[0]?.textContent || "";
                        const matrNr = String(matrikel.getElementsByTagName("Matrikelnummer")[0]?.textContent ?? "")?.replace(/^0*/, "");
                        return (
                           <Fragment key={idx}>
                              <KeyValueLine valKey={"Matnr. " + matrNr} value={name} keepEmpty />
                           </Fragment>
                        )
                     })}
                  </KeyValuesBoxGrid>
               </ValueGroup>
            </Fragment>
         ))}
      </GlassFoldUdBox>
   )
}

function ServitutTekstGrupper({ servitutXDOC }: { servitutXDOC: Element }) {
   let header = servitutXDOC.getElementsByTagName("Overskrift")[0];
   let paragraphs = servitutXDOC.getElementsByTagName("Afsnit");
   return (<>
      {header ? (
         <h1>
            {header.textContent}
         </h1>
      ) : null}
      {[...paragraphs].map((p, idx) => (
         <p key={idx} >
            {p.textContent}
         </p>
      ))}
   </>
   )
}

function ServitutTekst({ servitutXDOC }: { servitutXDOC: Document | null }) {
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }
   let servitut = servitutXDOC.getElementsByTagName?.("Servitut")[0];
   let servitutTextGroups = [...servitut?.getElementsByTagName("TekstGruppe") || []];
   if (servitutTextGroups.length === 0) {
      return <></>
   }
   let firstTextGroup = servitutTextGroups[0];
   let header = firstTextGroup.getElementsByTagName("Overskrift")[0];
   let paragraphs = firstTextGroup.getElementsByTagName("Afsnit");
   return (
      <GlassFoldUdBox title={"Servitut - " + (header?.textContent || paragraphs[0]?.textContent)} foldetUd={false}>
         <PropertyTextDoc>
            {servitutTextGroups.map((tg, idx) => {
               return (
                  <Fragment key={idx}>
                     {(tg.children.length > 1 || idx != 0) && (
                        <ServitutTekstGrupper key={idx} servitutXDOC={tg} />
                     )}
                  </Fragment>
               )
            })}
         </PropertyTextDoc>
      </GlassFoldUdBox>
   )
}


function BilagsVisning({ servitutXDOC }: { servitutXDOC: Document | null }) {
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }
   let samling = servitutXDOC!.getElementsByTagName("BilagReferenceSamling")[0];
   if (!samling) return null;

   return (
      <GlassFoldUdBox title={Localization.getText("AppendixReference")} foldetUd={true}>
         {Array.from(samling.children).map((r: Element, idx: number) => {
            let ref = r.getElementsByTagName("BilagReference")[0]?.textContent || "";
            let kode = r.getElementsByTagName("BilagReferenceKode")[0]?.textContent || "";
            let beskrivelse = r.getElementsByTagName("Afsnit")[0]?.textContent || "";

            const linkUrl = ref ? `https://www.tinglysning.dk/tinglysning/rest/bilag/${ref}` : null;
            const lang = typeof Localization.getLanguage === 'function' ? Localization.getLanguage() : 'da';
            const translatedKode = kode ? (translationTableBilagsReferenceKode?.[lang]?.[kode] ?? translationTableBilagsReferenceKode?.['da']?.[kode] ?? kode) : null;

            return (
               <Fragment key={ref}>
                  <KeyValueLine valKey={
                     linkUrl ? (
                        <a href={linkUrl} target="_blank" rel="noopener noreferrer" >
                           Bilag {idx + 1}
                        </a>
                     ) : (
                        beskrivelse || Localization.getText("No data")
                     )
                  } value={<>
                     <span>
                        {beskrivelse}
                     </span><br />
                     <span>
                        {translatedKode}
                     </span>
                  </>} infoText={ref} />
               </Fragment>
            );
         })}
      </GlassFoldUdBox>
   );
}

const handleCoordinates = async (data: {
   BFEnummer: number,
   SFEnummer?: string | number, // finde center of sfe, 
   type: "SFE" | "BPFG" | "Ejerlejlighed",
   geometri?: string, //WKT string
}): Promise<any> => {
   if (data.geometri) {
      let tmp = WKTparse(data.geometri)
      if (tmp?.coordinates[0]) {
         let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
         return coords;
      }
   }
   if ("geometri" in data) {
      delete data.geometri;
   }
   if (data.type) {
      let EBR = await PropertyInfoInterface.EBRadresseForBFE(data.BFEnummer.toString());
      let geometri = EBR.features?.[0]?.properties?.adresse?.[0]?.husnummer?.adgangspunkt?.position;
      if (geometri) {
         let tmp = WKTparse(geometri)
         if (tmp?.coordinates[0]) {
            let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
            return coords;
         }
      }
      let geometri2 = EBR.features?.[0]?.properties?.husnummer?.[0]?.adgangspunkt?.position
      if (geometri2) {
         let tmp = WKTparse(geometri2)
         if (tmp?.coordinates[0]) {
            let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
            return coords;
         }
      }
   }
   if (data.type == "SFE") {
      let geoJson = await PropertyInfoInterface.getGeojsonOfSFE(data.BFEnummer);
      let coords = polylabel(geoJson.features[0].geometry.coordinates, 0.00001)
      return coords;
   }
};

// Translation table for BilagReferenceKode (updated)
const translationTableBilagsReferenceKode: any = {
   da: {
      "1": "Udlægsattest, udstedt før 25. marts 2008",
      "2": "Arrestattest, udstedt før 25. marts 2008",
      "3": "Ejendomsdom, med ankepåtegning, afsagt før 25. marts 2008",
      "4": "Modifikationsdom, med ankepåtegning, afsagt før 25. marts 2008",
      "5": "Auktionsskøde med evt. transportskøde",
      "6": "Bobestyrerattest, udstedt før 25. marts 2008",
      "7": "Kuratorattest, udstedt før 25. marts 2008",
      "8": "Tilladelse til erhvervelse af fast ejendom i Danmark, udstedt af JM",
      "9": "Tilladelse til erhvervelse, udstedt af Statsamt",
      "10": "Tilladelse fra Skov- og Naturstyrelsen om selskabs erhvervelse af fast ejendom",
      "11": "Dokumentation fra menighedsråd om tegningsreglerne for rådet",
      "12": "Attestation fra Direktoratet for Fødevareerhverv om behørig afgivelse af erklæringer i medfør af landbrugslovens erhvervsbestemmelser",
      "13": "Godkendelse fra Kirkeministeriet eller Stiftsøvrigheden til salg eller pantsætning af jordelig kirkelig gods",
      "14": "Tilladelse til erhvervelse af arbejderbolig",
      "15": "Stiftelsesdokumenter for selskab under stiftelse",
      "16": "Anden dokumentation for, at anmeldelse af nystiftet selskab er sket overfor Erhvervs- og Selskabsstyrelsen indenfor de i lovens krævede frister",
      "17": "Kommunalbestyrelsens samtykke til salg af ejendom fra Almen boligorganisation",
      "18": "Matrikelkort med attestationen \"e-rids\" (ved salg af nyudstykket parcel)",
      "19": "Dokumentation for generalforsamling om tegningsregler m.v. for forening",
      "20": "Vedtægter m.v. for forening",
      "21": "Interessentskabskontrakt med tegningsregler m.v.",
      "22": "Dispensation eller tilladelse fra Jordbrugskommissionen vedr. erhvervelse af landbrugsejendom",
      "23": "Skiftefuldmagter, udstedt før 25. marts 2008",
      "24": "Notering af umyndiggørelse på fast ejendom",
      "25": "Notering af ægtepagt som særeje",
      "26": "Tinglysning af ægtepagt som adkomst",
      "27": "Meddelelse om konkurs, udstedt før 25. marts 2008",
      "28": "Samme vedr. gældssanering, tvangsakkord",
      "29": "Tinglysning af vielsesattest rådighedsindskrænkende",
      "30": "Prokura, gældende før 25. marts 2008",
      "31": "Samtykke fra indehaver af forkøbsret eller tilbagekøbsret",
      "32": "Original bebestyrerattest, hvis en ejendom sælges af bobestyrer",
      "33": "Original skifteretsattest, hvis ejendom sælges af et dødsbo",
      "34": "Godkendelse fra kommunalbestyrelsen til tinglysning af servitut, jf. planlovens § 42",
      "35": "Samtykke fra bortforpagtning af landbrugsejendom fra Direktoratet for Fødevareerhverv",
      "36": "Godkendelse af pantsætning fra værge",
      "37": "Godkendelse af pantsætning fra Almennyttige boligselskaber",
      "38": "GML-dokument med koordinater til servitutter og bygning på lejet grund",
      "80": "Andet",
      "90": "Intern brug"
   },
   en: {
      "1": "Certificate of Execution issued prior to 25 March 2008",
      "2": "Certificate of Attachment issued prior to 25 March 2008",
      "3": "Judgment concerning real property, with endorsement of appeal, rendered prior to 25 March 2008",
      "4": "Modification judgment, with endorsement of appeal, rendered prior to 25 March 2008",
      "5": "Deed of Auction, including any Deed of Assignment",
      "6": "Certificate of Estate Administration issued prior to 25 March 2008",
      "7": "Trustee’s Certificate issued prior to 25 March 2008",
      "8": "Permit for acquisition of real property in Denmark issued by the Ministry of Justice",
      "9": "Permit for acquisition issued by the County Authority (Statsamt)",
      "10": "Authorization from the Danish Forest and Nature Agency regarding corporate acquisition of real property",
      "11": "Documentation from the Parish Council concerning the Council’s signing authority",
      "12": "Attestation from the Directorate for Food Business confirming proper submission of declarations pursuant to the agricultural law’s business provisions",
      "13": "Approval from the Ministry of Ecclesiastical Affairs or the Diocesan Authority for sale or mortgaging of church-owned land",
      "14": "Permit for acquisition of workers’ housing",
      "15": "Founding documents for a company under incorporation",
      "16": "Other documentation evidencing that notification of a newly incorporated company has been duly submitted to the Danish Commerce and Companies Agency within the statutory deadlines",
      "17": "Consent from the Municipal Council for sale of property by a Public Housing Organization",
      "18": "Cadastral map with the endorsement 'e-plan' (for sale of newly subdivided parcel)",
      "19": "Documentation from the general assembly concerning the association’s signing authority, etc.",
      "20": "Articles of Association and related documents for an association",
      "21": "Partnership Agreement including signing authority, etc.",
      "22": "Exemption or permit from the Agricultural Commission concerning acquisition of agricultural property",
      "23": "Probate Power of Attorney issued prior to 25 March 2008",
      "24": "Registration of legal incapacitation relating to real property",
      "25": "Registration of prenuptial agreement establishing separate property",
      "26": "Registration of prenuptial agreement as title",
      "27": "Notice of bankruptcy issued prior to 25 March 2008",
      "28": "Same concerning debt restructuring or compulsory composition",
      "29": "Registration of marriage certificate restricting right of disposal",
      "30": "Power of Procuration valid prior to 25 March 2008",
      "31": "Consent from holder of pre-emption or repurchase right",
      "32": "Original Certificate of Estate Administration where property is sold by an estate administrator",
      "33": "Original Certificate from the Probate Court where property is sold by an estate",
      "34": "Approval from the Municipal Council for registration of easement pursuant to section 42 of the Danish Planning Act",
      "35": "Consent from the Directorate for Food Business concerning lease of agricultural property",
      "36": "Approval of mortgage granted by legal guardian",
      "37": "Approval of mortgage granted by non-profit housing associations",
      "38": "GML document containing coordinates for easements and buildings on leased land",
      "80": "Other",
      "90": "Internal use"
   }
};